/**
 * ClientDetailPanel — shell component for the client detail slide-out
 *
 * Follows LeaseDetailPanel pattern:
 * - Reads detailPanelMode from store → renders as Sheet (sidebar 45vw) or Dialog (modal)
 * - Accepts either direct client data or clientId for fetch
 * - Shows loading skeleton while data loads
 * - Nested detail panels for leases, transient stays, and aircraft
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useClientsRegistryStore } from '@/stores/clientsRegistryStore'
import { useClientDetail } from '@/hooks/useClientDetail'
import { ClientDetailContent } from './ClientDetailContent'
import { LeaseDetailPanel } from '@/components/leases/LeaseDetailPanel'
import { TransientStayDetailPanel } from '@/components/transient-stays/TransientStayDetailPanel'
import { AircraftDetailPanel } from '@/components/aircrafts/AircraftDetailPanel'
import type { ClientRegistryRow } from '@/types/clients'
import { getClientDisplayName } from '@/types/clients'

// ============================================
// Props
// ============================================

interface ClientDetailPanelProps {
  /** Direct data from table row — avoids re-fetch */
  client?: ClientRegistryRow | null
  /** Fallback: fetch detail data by ID */
  clientId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit: (id: string) => void
  onManageContacts: (id: string) => void
}

// ============================================
// Loading Skeleton
// ============================================

function DetailSkeleton() {
  return (
    <div className="space-y-5 p-1">
      <div className="space-y-2">
        <Skeleton className="h-6 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-full" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function ClientDetailPanel({
  client,
  clientId,
  open,
  onOpenChange,
  onEdit,
  onManageContacts,
}: ClientDetailPanelProps) {
  const navigate = useNavigate()
  const { detailPanelMode, setDetailPanelMode } = useClientsRegistryStore()

  // Nested detail panel state
  const [selectedLeaseId, setSelectedLeaseId] = useState<string | null>(null)
  const [selectedTransientId, setSelectedTransientId] = useState<string | null>(null)
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(null)

  // Fetch nested detail data (contacts, leases, transients, aircraft)
  const resolvedId = client?.id ?? clientId ?? null
  const detail = useClientDetail(open ? resolvedId : null)

  const handleToggleMode = () => {
    setDetailPanelMode(detailPanelMode === 'sidePanel' ? 'modal' : 'sidePanel')
  }

  const clientName = client ? getClientDisplayName(client) : ''

  const handleViewContracts = useCallback(() => {
    if (clientName) {
      navigate(`/leases?search=${encodeURIComponent(clientName)}`)
      onOpenChange(false)
    }
  }, [navigate, clientName, onOpenChange])

  const handleViewAircraft = useCallback(() => {
    if (clientName) {
      navigate(`/aircrafts?search=${encodeURIComponent(clientName)}`)
      onOpenChange(false)
    }
  }, [navigate, clientName, onOpenChange])

  const content = !client ? (
    <DetailSkeleton />
  ) : detail.isLoading ? (
    <DetailSkeleton />
  ) : (
    <ClientDetailContent
      client={client}
      contacts={detail.contacts}
      leases={detail.leases}
      transients={detail.transients}
      aircraft={detail.aircraft}
      panelMode={detailPanelMode}
      onToggleMode={handleToggleMode}
      onEdit={onEdit}
      onManageContacts={onManageContacts}
      onLeaseClick={setSelectedLeaseId}
      onTransientClick={setSelectedTransientId}
      onAircraftClick={setSelectedAircraftId}
      onViewContracts={handleViewContracts}
      onViewAircraft={handleViewAircraft}
    />
  )

  return (
    <>
      {detailPanelMode === 'modal' ? (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>Client Details</DialogTitle>
              <DialogDescription>Detailed view of client information</DialogDescription>
            </DialogHeader>
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="right"
            className="sm:max-w-md w-[45vw]"
            showCloseButton
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Client Details</SheetTitle>
              <SheetDescription>Detailed view of client information</SheetDescription>
            </SheetHeader>
            <ScrollArea className="flex-1 px-4 pb-4">
              {content}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      {/* Nested Detail Panels */}
      <LeaseDetailPanel
        leaseId={selectedLeaseId ?? undefined}
        open={!!selectedLeaseId}
        onOpenChange={(open) => { if (!open) setSelectedLeaseId(null) }}
        onEdit={() => {}}
      />

      <TransientStayDetailPanel
        stayId={selectedTransientId ?? undefined}
        open={!!selectedTransientId}
        onOpenChange={(open) => { if (!open) setSelectedTransientId(null) }}
      />

      <AircraftDetailPanel
        aircraftId={selectedAircraftId ?? undefined}
        open={!!selectedAircraftId}
        onOpenChange={(open) => { if (!open) setSelectedAircraftId(null) }}
        onEdit={() => {}}
      />
    </>
  )
}
